import useSWR from 'swr';
import { FileText, Download, Clock } from 'lucide-react';
import apiClient from '@/lib/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PayrollDoc {
  id: string;
  nombre: string;
  filename: string;
  mimetype: string;
  size_bytes: number;
  ocr_status: string;
  validation_status: string;
  category_nombre: string | null;
  created_at: string;
}

interface ListResult {
  data: PayrollDoc[];
  total: number;
}

// ── Fetcher ───────────────────────────────────────────────────────────────────

async function fetcher(url: string): Promise<ListResult> {
  const res = await apiClient.get<ListResult>(url);
  return res.data;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
}

/** Mes y año en formato "Marzo 2025" extraído de la fecha de subida. */
function formatMonthYear(iso: string): string {
  return new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric' }).format(new Date(iso));
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Devuelve true si la fecha ISO está dentro de los últimos 7 días. */
function isNew(iso: string): boolean {
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return new Date(iso).getTime() > sevenDaysAgo;
}

// ── Componente ────────────────────────────────────────────────────────────────

export function MyPayrollPage() {
  const { data, isLoading, error } = useSWR<ListResult>(
    '/documents/my-payroll',
    fetcher,
    { refreshInterval: 120_000 },
  );

  const docs = data?.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)', margin: 0 }}>
          Mis nominas y documentos
        </h1>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-muted)', marginTop: '0.25rem' }}>
          Documentos laborales vinculados a tu perfil de empleado.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
          border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)',
          borderRadius: 8, padding: '0.875rem 1rem',
          fontSize: '0.8125rem', color: 'var(--color-danger)',
        }}>
          No se han podido cargar tus documentos. Comprueba la conexion con el servidor.
        </div>
      )}

      {/* Table */}
      <div style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 10, overflow: 'hidden',
      }}>
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)', fontSize: '0.875rem' }}>
            Cargando documentos...
          </div>
        ) : docs.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <FileText size={32} style={{ color: 'var(--color-muted)', margin: '0 auto 0.75rem' }} />
            <p style={{ fontSize: '0.875rem', color: 'var(--color-muted)', margin: 0 }}>
              Todavia no tienes documentos vinculados a tu perfil.
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-table-header)' }}>
                {['Mes / Ano', 'Documento', 'Tamano', 'Estado', 'Subido el'].map(h => (
                  <th key={h} style={{ padding: '0.625rem 1rem', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                ))}
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {docs.map((doc, i) => {
                const nuevo = isNew(doc.created_at);
                return (
                <tr key={doc.id} style={{ borderBottom: i < docs.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                  {/* Columna Mes/Año */}
                  <td style={{ padding: '0.875rem 1rem', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)', textTransform: 'capitalize' }}>
                        {formatMonthYear(doc.created_at)}
                      </span>
                      {nuevo && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 7px', borderRadius: 999, fontSize: '0.6875rem', fontWeight: 700, background: 'color-mix(in srgb, var(--color-primary) 15%, transparent)', color: 'var(--color-primary)', letterSpacing: '0.04em' }}>
                          NUEVO
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Nombre del archivo */}
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileText size={15} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-text)' }}>{doc.nombre}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                          {doc.mimetype === 'application/pdf' ? 'PDF' : doc.mimetype.split('/')[1]?.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.8125rem', color: 'var(--color-muted)' }}>
                    {formatSize(doc.size_bytes)}
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    {doc.validation_status === 'verified' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>
                        Verificado
                      </span>
                    ) : doc.ocr_status === 'done' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>
                        Listo
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: 'rgba(100,116,139,0.12)', color: '#94a3b8' }}>
                        <Clock size={10} /> Procesando
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.875rem 1rem', fontSize: '0.8125rem', color: 'var(--color-muted)' }}>
                    {formatDate(doc.created_at)}
                  </td>
                  <td style={{ padding: '0.875rem 1rem' }}>
                    <a
                      href={`/api/v1/documents/${doc.id}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Descargar / ver documento"
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-muted)', textDecoration: 'none' }}
                    >
                      <Download size={14} />
                    </a>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {data && (
        <p style={{ fontSize: '0.75rem', color: 'var(--color-muted)', margin: 0 }}>
          {data.total} documento{data.total !== 1 ? 's' : ''} en total
        </p>
      )}
    </div>
  );
}
